import { ErrorResponse, LocalDate, NumberDate, Timestamp } from '@lemon/core/model/common'
import { CurrencyRateData } from '@lemon/core/model/currency'
import { Bic } from '@lemon/core/service/bic.service'

export interface Pankki {
  avain: Bic | 'OMASP'
  nimi: string
}

export interface Tili {
  avain: string
  pankinAvain: string
  iban: string
  bic: string
}

export interface TilitapahtumanLisatiedot {
  /**
   * Raakadata, jonka olemme saaneet, xml / html / json
   */
  raakadata: any

  /** The plain holvi is some old, but can still be found from data though not from code. */
  /** Käsin (eg. manually) is some old, but can still be found from data though not from code. */
  /** Nordigen is some old, but can still be found from data though not from code. */
  lahde: 'kasin' | 'camt.053' | 'viia' | 'holviapi' | 'holvi' | 'nordigen' | 'enable-banking' | 'psd2-provider-change'

  // Reskontra
  kohdistukset?: TilitapahtumanKohdistusLaskuun[]
}

export const TILITAPAHTUMAN_TILAT = ['final', 'unconfirmed'] as const

export type TilitapahtumanTila = (typeof TILITAPAHTUMAN_TILAT)[number]

export const TILITAPAHTUMAN_KANAVAT = ['ws-camt', 'aiia', 'holviapi', 'nordigen', 'enable-banking'] as const

export type TilitapahtumanKanava = (typeof TILITAPAHTUMAN_KANAVAT)[number]

export type ValuuttakonversionTiedotKirjauksessa = ValuuttakonversionTiedot | ValuuttakonversionTiedotTilitapahtumassa

export interface ValuuttakonversionTiedot {
  kurssi: CurrencyRateData
  hakupaiva: LocalDate
  hakuaikaleima: Timestamp
  lahdevaluutta: string
  kohdevaluutta: string
}

export interface ValuuttakonversionTiedotTilitapahtumassa extends ValuuttakonversionTiedot {
  alkuperainenSaldo: number
  alkuperainenSumma: number
}

export interface Tilitapahtuma {
  avain: string

  /** Key to identify the Pankki that this transaction is part of */
  pankinAvain: string

  /** Iban kuten ISO 13616 määrittää */
  iban: string

  /** The BIC code like NDEAFIHH */
  bic: Bic

  /**
   * vvvvkkppxxxxx
   */
  jarjestys: number

  /**
   * The SEPA archival id
   */
  archivalId: string

  /**
   * The id that the bank has produced for this Tilitapahtuma. From PSD2 interface.
   */
  bankId: string

  /**
   * The id that the integration gateway (Like Viia) has assigned.
   */
  dataProviderId: string

  summa: number
  saldo: number

  /** The three letter currency identifier such as EUR according to ISO 4217 */
  valuutta: string

  /** The currency conversion details if converted from foreign currency to euros */
  valuuttakonversio?: ValuuttakonversionTiedotTilitapahtumassa

  pvm: Timestamp
  pvml: LocalDate
  p: NumberDate

  lisatiedot: string

  /**
   * Lähtevissä maksuissa se, kenelle tältä tililtä maksetaan,
   * saapuvissa se, kuka maksoi tälle tilille
   */
  maksaja: string
  /**
   * Lähtevissä maksuissa se, mille tilille tältä tililtä maksetaan,
   * saapuvissa se, mistä tililtä maksettiin tälle tilille
   */
  maksajanIban: string
  /**
   * Lähtevissä maksuissa se, mille tilille tältä tililtä maksetaan,
   * saapuvissa se, mistä tililtä maksettiin tälle tilille
   */
  maksajanBic: string
  viesti: string
  viite: string

  poistettu: boolean
  tila: TilitapahtumanTila

  yliajoviite?: string
  kohdistamatta?: number
  kohdistettu?: number

  tagTositteet?: string[]
  tagRaahatut?: string[]
  tagLaskut?: string[]

  /**
   * Jos maksu on osa maksunippua, tämän nipun yksilöivä tunniste.
   * Esim. WS-maksut ja sala-maksut.
   */
  nippuId: string

  /**
   * Mistä kanavasta tilitapahtuma on tullut?
   */
  kanava: TilitapahtumanKanava
}

export interface TilitapahtumanKohdistusLaskuun {
  avain: string
  laskuAvain: string
  laskunro: string
  laskunsaaja: string
  suoritus: number
  luotu: Timestamp
  vastaparinAvain: string
}

export interface LataaCamt053DokuPyynto {
  asiakasId: string
  asiakasAvain: string
  base64File: string
}

export interface LataaCamt053DokuVastaus {
  onnistui: boolean
  tallennettiinPankkeja: number
  tallennettiinTileja: number
  tallennettiinTapahtumia
}

export interface TilitapahtumanTuontityodata {
  tilitapahtuma: Tilitapahtuma
  lisatty: Timestamp
  lisatiedot: TilitapahtumanLisatiedot
  kohdistaUudelleen?: true
  yliajoviite?: string
}

export interface TilitapahtumanTuontityodataJaAsiakasTiedot extends TilitapahtumanTuontityodata {
  asiakasAvain: string
  asiakasId: number
}

export enum BankBalanceCalcAlgorithm {
  AIIA_OLD = 'aiia-old',

  VERSION_ONE = 'v1',
}

export interface BankBalance {
  avain: string
  iban: string
  saldo: number
  paiva: NumberDate
  source: 'aiia' | 'tiliote' | 'holvi' | 'camt.053-bank' | 'camt.053-calculated' | 'enable-banking'
  currency: string
  edited: Timestamp
  algoVersion: BankBalanceCalcAlgorithm
}

export interface BankBalancesPerMonth {
  /**
   * Format yymm
   */
  kuukausi: number
  asiakasAvain: string
  iban: string
  /**
   * Key is 'p' + day number
   */
  saldot: { [day: string]: BankBalance }
  /**
   * Kunkin lähteen viimeisin saldo päivittäin, josta saldot-kenttään valitaan päivän saldo. Avain kuten saldoissa, sen alla lähde.
   */
  lahteet?: { [day: string]: { [source: string]: BankBalance } }
  /**
   * Päivän valitun saldon myöhästyneet muutokset: kirjataan, kun aiempi saldo oli tallennettu päivän päätyttyä. Avain kuten saldoissa.
   */
  muutokset?: { [day: string]: BankBalanceMuutos[] }
  algoVersion: BankBalanceCalcAlgorithm
}

export interface BankBalanceMuutos {
  aika: Timestamp
  saldo: number
  aiempiSaldo: number
  lahde: BankBalance['source']
}

export interface BankBalanceAddManuallyRequest {
  asiakasAvain: string
  iban: string
  date: NumberDate
  balance: number
  source: 'tiliote'
}

export interface BankBalanceAddManuallyResponse extends ErrorResponse<'missing-data' | 'unknown-error'> {}

export enum SahkoisenTiliotteenAlatyyppi {
  HOLVI_API = 'holvi',

  AIIA = 'aiia',

  ENABLE_BANKING = 'enable banking',

  NORDIGEN = 'nordigen',

  WEB_SERVICES = 'ws',
}

export interface LatestBankBalance {
  balance: number
  fetchTime: Timestamp
}
